import { type CSSProperties, type ReactNode, useMemo } from 'react';

import { toLines, tokenize } from '@shared/docs/norrsken/tokenize';

import css from './styles.module.css';

interface NorrskenProps {
	content: string;

	language?: string;
}

/**
 *	A fenced code block, coloured and numbered. Rendered from the fence node
 *	override in @shared/docs/markdoc, never written by hand.
 *
 *	The colouring is part of the rendered tree rather than something an effect
 *	paints on afterwards, so a block is never briefly plain, `<` and `>` in the
 *	source are React's problem rather than an escaping hazard, and nothing
 *	writes innerHTML.
 *
 *	Each line is its own row, so a long one wraps under itself instead of
 *	scrolling the block sideways, and keeps the one number it started with. The
 *	numbers are a CSS counter rather than text: there is nothing in the markup
 *	for a reader to select, so copying a block gives back the code alone.
 */
export function Norrsken({ content, language }: NorrskenProps): ReactNode {
	const lines = useMemo(
		() => toLines(tokenize(content, language)),
		[content, language]
	);

	// Room for the widest number the block actually reaches.
	const gutter = {
		'--norrsken-gutter': `${String(lines.length).length}ch`,
	} as CSSProperties;

	return (
		<pre
			data-norrsken={language ?? 'plain'}
			data-language={language}
			className={css.block}
			style={gutter}>
			{lines.map((line, index) => (
				<span key={index} className={css.line}>
					<span className={css.code}>
						{line.map((token, position) =>
							token.name ? (
								<span key={position} className={css[token.name]}>
									{token.value}
								</span>
							) : (
								token.value
							)
						)}
					</span>
				</span>
			))}
		</pre>
	);
}
